import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Cpu, Printer, Scan, Cog, Check, ArrowRight } from "lucide-react";
import axios from "axios";
import Header from "../components/Header";
import ChatBot from "../components/ChatBot";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TechnologyPage = () => {
  const [content, setContent] = useState({
    tech_title: "Dijital",
    tech_highlight: "Üretim Teknolojisi",
    tech_description: "En son CAD/CAM teknolojisi ve 3D yazıcılarla mikron seviyesinde hassasiyet sağlıyoruz.",
    tech_hero_image: "",
    tech1_title: "CAD/CAM Sistemleri",
    tech1_desc: "3Shape ve Exocad yazılımları ile dijital tasarım.",
    tech2_title: "3D Yazıcılar",
    tech2_desc: "Yüksek çözünürlüklü 3D baskı teknolojisi.",
    tech3_title: "CNC Freze",
    tech3_desc: "Hassas CNC freze ile zirkonyum işleme.",
    tech4_title: "Dijital Tarayıcılar",
    tech4_desc: "Model ve ağız içi dijital tarama."
  });
  const [selectedTech, setSelectedTech] = useState(null);

  useEffect(() => {
    fetchContent();
  }, []);

  const fetchContent = async () => {
    try {
      const response = await axios.get(`${API}/content`);
      setContent(prev => ({ ...prev, ...response.data }));
    } catch (error) {
      console.error("Error fetching content:", error);
    }
  };

  const getImageUrl = (url) => {
    if (!url) return "";
    return url.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${url}` : url;
  };

  const technologies = [
    {
      icon: <Cpu className="w-8 h-8" />,
      title: content.tech1_title,
      description: content.tech1_desc,
      details: "CAD/CAM sistemleri dijital iş akışımızın kalbidir. Ağız içi ve model tarama verilerini alarak, bilgisayar ortamında mikron seviyesinde restorasyon tasarlarız. 3Shape ve Exocad yazılımlarıyla en karmaşık vakaları bile yüksek doğrulukla planlarız.",
      features: [
        "3Shape Dental System & Exocad DentalCAD yazılımları",
        "Kron, köprü, implant üstü ve gülüş tasarımı",
        "Dijital artikülatör ile fonksiyonel uyum kontrolü",
        "Mikron seviyesinde hassasiyet",
        "Hızlı tasarım ve kısa teslim süresi",
      ],
    },
    {
      icon: <Printer className="w-8 h-8" />,
      title: content.tech2_title,
      description: content.tech2_desc,
      details: "Yüksek çözünürlüklü reçine bazlı 3D yazıcılarımızla; çalışma modelleri, cerrahi rehberler, geçici protezler ve mock-up'ları hızlı ve tekrarlanabilir şekilde üretiriz. Biyouyumlu reçinelerle güvenli ve hassas sonuçlar elde ederiz.",
      features: [
        "Model, cerrahi splint ve geçici protez üretimi",
        "Biyouyumlu model, temp ve mock-up reçineleri",
        "25–50 mikron katman çözünürlüğü",
        "Aynı gün prototip ve hızlı üretim",
        "Tekrarlanabilir, standart kalite",
      ],
    },
    {
      icon: <Cog className="w-8 h-8" />,
      title: content.tech3_title,
      description: content.tech3_desc,
      details: "Çok eksenli CNC freze ünitelerimiz zirkonyum, PMMA, cam seramik ve metal blokları mikron hassasiyetinde işler. Yüksek kenar uyumu ve pürüzsüz yüzey ile üstün estetik ve dayanıklılık sağlarız.",
      features: [
        "4 ve 5 eksen kuru & yaş freze üniteleri",
        "Zirkonyum, PMMA, cam seramik ve Co-Cr işleme",
        "Yüksek kenar uyumu ve pürüzsüz yüzey",
        "Multilayer zirkonyum desteği",
        "Otomatik blok değişimi ile yüksek kapasite",
      ],
    },
    {
      icon: <Scan className="w-8 h-8" />,
      title: content.tech4_title,
      description: content.tech4_desc,
      details: "Masaüstü model ve ağız içi tarayıcılarla hassas dijital ölçü alırız. Klasik ölçünün deformasyon ve hata riskini ortadan kaldırır, kliniklerle dijital veri paylaşımını mümkün kılarız.",
      features: [
        "Masaüstü model tarayıcı + ağız içi tarama uyumu",
        "Mikron seviyesinde yüksek doğruluk",
        "STL veri ile hızlı dijital iş akışı",
        "Klasik ölçü hatalarının eliminasyonu",
        "Klinikle güvenli dijital veri paylaşımı",
      ],
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero */}
      <section className="relative py-20 md:py-28 overflow-hidden" data-testid="tech-hero">
        <div className="absolute inset-0 bg-gradient-to-br from-[#0A192F] via-[#0d2341] to-[#0A192F]" />
        <div className="absolute top-10 right-10 w-72 h-72 bg-gradient-to-r from-cyan-400 to-blue-500 rounded-full blur-3xl opacity-20 animate-pulse" />
        <div className="absolute bottom-10 left-10 w-96 h-96 bg-gradient-to-r from-blue-400 to-cyan-300 rounded-full blur-3xl opacity-15" />
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center lg:text-left"
            >
              <p className="text-cyan-400 text-sm font-medium tracking-widest uppercase mb-4">
                Teknoloji
              </p>
              <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
                {content.tech_title}{" "}
                <span className="bg-gradient-to-r from-cyan-400 via-blue-400 to-cyan-300 bg-clip-text text-transparent">
                  {content.tech_highlight}
                </span>
              </h1>
              <p className="text-white/70 text-lg max-w-2xl">
                {content.tech_description}
              </p>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
              className="hidden lg:block"
            >
              <img
                src={getImageUrl(content.tech_hero_image)}
                alt="Teknoloji"
                className="rounded-2xl shadow-2xl w-full h-[350px] object-cover"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Technologies */}
      <section className="py-20 md:py-28 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-[#0A192F] mb-4">
              Ekipman ve Yazılımlarımız
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Sektörün en gelişmiş teknolojileri ile üstün kalite ve hassasiyet.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {technologies.map((tech, index) => (
              <motion.button
                type="button"
                key={index}
                onClick={() => setSelectedTech(tech)}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl hover:-translate-y-1 transition-all duration-300 text-center group cursor-pointer border-2 border-transparent hover:border-cyan-400 focus:outline-none focus:border-cyan-500"
                data-testid={`tech-card-${index}`}
              >
                <div className="w-16 h-16 bg-gradient-to-br from-cyan-500 to-blue-500 rounded-2xl flex items-center justify-center text-white mx-auto mb-4 group-hover:scale-110 transition-transform">
                  {tech.icon}
                </div>
                <h3 className="font-heading text-lg font-semibold text-[#0A192F] mb-2">
                  {tech.title}
                </h3>
                <p className="text-slate-600 text-sm">
                  {tech.description}
                </p>
                <span className="mt-4 inline-flex items-center gap-1 text-cyan-600 text-sm font-medium group-hover:gap-2 transition-all">
                  Daha fazla bilgi
                  <ArrowRight className="w-4 h-4" />
                </span>
              </motion.button>
            ))}
          </div>
        </div>
      </section>

      {/* Detay Modalı */}
      <Dialog open={!!selectedTech} onOpenChange={(open) => !open && setSelectedTech(null)}>
        <DialogContent className="sm:max-w-lg" data-testid="tech-detail-modal">
          {selectedTech && (
            <>
              <DialogHeader>
                <div className="w-16 h-16 bg-gradient-to-br from-cyan-500 to-blue-500 rounded-2xl flex items-center justify-center text-white mb-4">
                  {selectedTech.icon}
                </div>
                <DialogTitle className="font-heading text-2xl text-[#0A192F]" data-testid="tech-detail-title">
                  {selectedTech.title}
                </DialogTitle>
                <DialogDescription className="text-slate-600 text-base pt-2">
                  {selectedTech.details}
                </DialogDescription>
              </DialogHeader>
              <div className="mt-2">
                <h4 className="font-semibold text-[#0A192F] mb-3 text-sm uppercase tracking-wide">
                  Öne Çıkanlar
                </h4>
                <ul className="space-y-2">
                  {selectedTech.features.map((f, i) => (
                    <li key={i} className="flex items-start gap-3 text-slate-700 text-sm" data-testid={`tech-feature-${i}`}>
                      <span className="mt-0.5 w-5 h-5 rounded-full bg-cyan-100 text-cyan-600 flex items-center justify-center flex-shrink-0">
                        <Check className="w-3 h-3" />
                      </span>
                      {f}
                    </li>
                  ))}
                </ul>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      <ChatBot />
    </div>
  );
};

export default TechnologyPage;
